import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import QuagganEmoji from "./QuagganEmoji.jsx";

const normalizeName = (value) => String(value || "").trim().toLowerCase();

export default function NonparticipantSelectWindow({
  visible,
  nonparticipants,
  onClose,
  onFinishSelection,
}) {
  const [search, setSearch] = useState("");
  const [pendingNames, setPendingNames] = useState([]);

  useEffect(() => {
    if (!visible) return;
    setSearch("");
    setPendingNames([]);
  }, [visible]);

  const filteredNonparticipants = useMemo(() => {
    const query = search.trim().toLowerCase();
    return nonparticipants.filter((nonparticipant) =>
      normalizeName(nonparticipant?.name).includes(query),
    );
  }, [nonparticipants, search]);

  const pendingKeys = useMemo(
    () => new Set(pendingNames.map(normalizeName)),
    [pendingNames],
  );

  const addToPending = (nonparticipant) => {
    const name = String(nonparticipant?.name || "").trim();
    if (!name || pendingKeys.has(normalizeName(name))) return;
    setPendingNames((previous) => [...previous, name]);
  };

  const removeFromPending = (name) => {
    setPendingNames((previous) =>
      previous.filter((entry) => normalizeName(entry) !== normalizeName(name)),
    );
  };

  if (!visible) return null;

  return createPortal(
    <>
      <div
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(2, 10, 30, 0.82)",
          backdropFilter: "blur(6px)",
          zIndex: 1350,
        }}
      />
      <div
        style={{
          position: "fixed",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1351,
          pointerEvents: "none",
        }}
      >
        <div
          onClick={(event) => event.stopPropagation()}
          style={{
            width: "min(980px, calc(100vw - 24px))",
            maxHeight: "min(82vh, 760px)",
            display: "flex",
            flexDirection: "column",
            borderRadius: 22,
            background: "linear-gradient(160deg, rgba(10, 35, 70, 0.98) 0%, rgba(3, 16, 42, 0.99) 100%)",
            border: "1px solid rgba(80, 180, 255, 0.38)",
            boxShadow: "0 0 40px rgba(0, 150, 255, 0.18), 0 8px 28px rgba(0, 0, 0, 0.72)",
            color: "#e0f7fa",
            pointerEvents: "auto",
            overflow: "hidden",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, padding: "22px 22px 12px", borderBottom: "1px solid rgba(144, 224, 239, 0.12)" }}>
            <div className="nonparticipant-list-scroll" style={{ maxHeight: "60vh", overflowY: "auto", paddingRight: 6 }}>
              <div style={{ fontSize: 22, fontWeight: 800, color: "#40e8ff" }}>Add nonparticipants</div>
              <p className="login-help-text" style={{ marginTop: 6 }}>
                Choose entries to add to the wheel, then click Finish.
              </p>
            </div>
            <button type="button" className="names-btn secondary" onClick={onClose}>Close</button>
          </div>

          <div className="nonparticipant-list-scroll" style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(220px, 0.7fr)", gap: 18, padding: 22, overflowY: "auto" }}>
            <div>
              <input
                type="text"
                className="login-input"
                placeholder="Search nonparticipants"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
              <div style={{ display: "grid", gap: 8, marginTop: 12 }}>
                {filteredNonparticipants.map((nonparticipant) => {
                  const name = String(nonparticipant?.name || "").trim();
                  const alreadyPending = pendingKeys.has(normalizeName(name));
                  return (
                    <div key={nonparticipant.id ?? name} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, padding: 10, borderRadius: 10, background: "rgba(8, 24, 50, 0.72)" }}>
                      <span style={{ color: "#eefbff", fontWeight: 700 }}>{name}</span>
                      <button type="button" className="names-btn secondary" onClick={() => addToPending(nonparticipant)} disabled={alreadyPending}>
                        {alreadyPending ? "Added" : "Add"}
                      </button>
                    </div>
                  );
                })}
              </div>
              {filteredNonparticipants.length === 0 ? (
                <p className="login-help-text">No nonparticipants found.</p>
              ) : null}
            </div>

            <div style={{ padding: 14, borderRadius: 14, background: "rgba(8, 24, 50, 0.62)", border: "1px solid rgba(144, 224, 239, 0.16)" }}>
              <div style={{ color: "#40e8ff", fontWeight: 800, marginBottom: 10 }}>
                About to add ({pendingNames.length})
              </div>
              {pendingNames.length > 0 ? (
                <div style={{ display: "grid", gap: 7 }}>
                  {pendingNames.map((name) => (
                    <div key={name} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                      <span style={{ color: "#eefbff" }}>{name}</span>
                      <button type="button" className="names-btn secondary" onClick={() => removeFromPending(name)}>Remove</button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="login-help-text">Nothing selected yet.</p>
              )}
            </div>
          </div>

          {nonparticipants.length === 0 ? (
            <>
              <p className="login-help-text" style={{ padding: "0 22px" }}>No nonparticipants are available.</p>
              <QuagganEmoji type="failure" />
            </>
          ) : null}

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, padding: "12px 22px 22px", borderTop: "1px solid rgba(144, 224, 239, 0.12)" }}>
            <button type="button" className="names-btn secondary" onClick={onClose}>Cancel</button>
            <button
              type="button"
              className="names-btn primary"
              onClick={() => onFinishSelection(pendingNames)}
              disabled={pendingNames.length === 0}
            >
              Finish
            </button>
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
}
